/**
 * The latent line between two points, on the client's side of it (`V143`-`V146`, P30).
 *
 * ## What this module is for
 *
 * Four questions, none of which needs the DOM and all of which the panel, `main.ts` and the tests
 * all ask: **what reference do I send for this point**, **how do I flatten a chain of them**,
 * **which steps of a walk go on the map**, and **how many existing marks will this run evict**.
 *
 * **Pure, and no DOM.** `marks.ts` and `trajectory.ts` are the precedent and the reason is the
 * same: the tests must be able to import the law without importing the renderer
 * (`oop_contracts §8`, enforced by `boundary.test.ts`).
 *
 * ## Why the endpoint type is `TrajectoryPoint` and not a new union
 *
 * The reader picks two points of any provenance, which is exactly the set `trajectory.ts` already
 * models — with keys, sequences, styles and origin labels that four surfaces already render. A
 * second union would be a second `keyOf`, a second `sequenceOf` and a second answer to "how is a
 * point of this provenance coloured", which is the distinction `P9.5` does not allow this app to
 * blur.
 *
 * So this module adds one function to that vocabulary — `latentRefOf` — and nothing else about a
 * point.
 *
 * ## The recipe, and why no point stores its latent
 *
 * Measured (`V138`, re-measured at `V144`): a 256-float latent is **5,243 characters** as the JSON
 * the server sends, **696** as base64 float16, against a ~300-byte record and a **5,242,000
 * character** origin budget measured in Chromium. Storing one on every decoded and encoded record
 * would be 7.99 MB at ten full views — **152% of the budget**, past which `commit` latches to
 * memory and the reader silently loses their history on reload.
 *
 * It is also unnecessary for three of the four provenances, which is the part that decided it:
 *
 * | point | what it already stores | how the server gets `z` | measured |
 * |---|---|---|---|
 * | corpus row | `row` | a memmap read | 0.009 ms |
 * | typed | the **sequence** | `model.encode`, `mu` never `rsample` | 4.7 ms |
 * | decoded | `xy` + the **fill** that made it | that fill, a pure function | 0.02–0.07 ms on PCA, **1,460 ms** for `inverse` on UMAP |
 * | **interpolated** | — | nothing, before this phase | — |
 *
 * So only the fourth needs anything, and it keeps a **recipe** — the leaves it was mixed from and
 * their weights, **170–478 characters** measured — rather than the vector. Linear interpolation is
 * closed under affine combination, so `flatten` merges a chain instead of nesting it and the term
 * count grows linearly rather than doubling per generation.
 *
 * **The cost, stated: this is exact only while the path rule is linear.** A slerp step is not an
 * affine combination of its endpoints, so a second path rule would need its own reference kind or
 * its vector kept. `DecodedMark.run.path` names the rule that produced a record for exactly that
 * reason.
 */

import type { InterpolationStep, LatentRef, LeafRef } from './api';
import type { DecodedMark } from './decoded';
import { CAP_PER_VIEW } from './decoded';
import type { TrajectoryPoint } from './trajectory';

/**
 * How many decimal places a stored weight keeps.
 *
 * Six, measured: at that precision a recipe reproduces its latent to **1.24e-6**, against
 * **9.63e-4** for storing the same vector as base64 float16 — which was itself measured to change
 * nothing in 200 real sequences. The recipe is the more faithful of the two options as well as the
 * smaller one, and rounding is what keeps it small.
 */
export const WEIGHT_PLACES = 6;

/** Round one weight to what is stored and sent. */
const roundWeight = (weight: number): number => Number(weight.toFixed(WEIGHT_PLACES));

/**
 * The wire reference for one point, and **the only place the mapping is spelled**.
 *
 * `null` for a point this app cannot name to the server — which today is exactly one case: a
 * decoded record produced by an interpolation *before* this phase existed, so it has
 * `result.fill === 'z'` and no `run` to carry a recipe. Those are old records, not a live failure
 * mode, and the panel refuses them with a sentence rather than sending a reference that would
 * resolve to the wrong latent.
 *
 * `checkpoint_sha` rides along on a recipe only. It is the claim "this was built under that
 * model", and the server answers 409 when the claim does not hold — a recipe is a promise to
 * recompute, and recomputing through a different model gives a different vector than the one that
 * produced the sequence on screen.
 */
export function latentRefOf(point: TrajectoryPoint): LatentRef | null {
  switch (point.kind) {
    case 'stored':
      return { row: point.datum.row };
    case 'encoded':
      return { sequence: point.mark.sequence };
    case 'generated':
      return refOfMark(point.mark);
  }
}

/** `latentRefOf`'s decoded arm, also reachable directly from a `DecodedMark`. */
export function refOfMark(mark: DecodedMark): LatentRef | null {
  const run = mark.run;
  if (run !== undefined) {
    return { combination: run.recipe, checkpoint_sha: run.checkpointSha };
  }
  const fill = mark.result.fill;
  // `row` is what `/decode` answers when a corpus row's own latent was read, so the reference is
  // that row rather than the coordinate it happened to be drawn at.
  if (fill === 'row' && mark.result.neighbour_row !== null) {
    return { row: mark.result.neighbour_row };
  }
  if (fill === 'nearest' || fill === 'inverse' || fill === 'hybrid') {
    return { xy: mark.xy, fill };
  }
  return null;
}

/**
 * The recipe for a point at `t` along the line from `start` to `end` (`V144`).
 *
 * **Flattening, not nesting.** Each endpoint contributes its own leaves scaled by its share, and
 * a leaf appearing on both sides is merged rather than listed twice — which is what keeps a chain
 * linear in term count instead of doubling it per generation.
 *
 * **The last weight is derived as `1 - sum(rest)`, and that is a correctness detail rather than
 * tidiness.** Rounding each weight independently breaks `sum = 1` by up to `n · 5e-7`, and the
 * server refuses a recipe whose weights do not sum to 1 because such a recipe is not a point on
 * any line between its terms. Deriving the last one makes the constraint hold by construction
 * rather than within a tolerance somebody has to pick.
 */
export function flatten(start: LatentRef, end: LatentRef, t: number): [LeafRef, number][] {
  const merged: [LeafRef, number][] = [];
  const add = (leaf: LeafRef, weight: number): void => {
    const key = leafKey(leaf);
    const found = merged.find(([held]) => leafKey(held) === key);
    if (found === undefined) merged.push([leaf, weight]);
    else found[1] += weight;
  };
  for (const [leaf, weight] of leavesOf(start)) add(leaf, weight * (1 - t));
  for (const [leaf, weight] of leavesOf(end)) add(leaf, weight * t);

  const kept = merged.filter(([, weight]) => weight !== 0);
  if (kept.length === 0) return [];
  const head = kept.slice(0, -1).map(([leaf, weight]): [LeafRef, number] => [leaf, roundWeight(weight)]);
  const tailLeaf = (kept[kept.length - 1] as [LeafRef, number])[0];
  const rest = head.reduce((total, [, weight]) => total + weight, 0);
  return [...head, [tailLeaf, Number((1 - rest).toFixed(WEIGHT_PLACES))]];
}

/** Every leaf a reference is made of, with its weight. A leaf reference is `[[itself, 1]]`. */
export function leavesOf(ref: LatentRef): [LeafRef, number][] {
  if (ref.combination !== undefined) return ref.combination.map(([leaf, w]) => [leaf, w]);
  const { row, sequence, xy, fill } = ref;
  const leaf: LeafRef = {};
  if (row !== undefined) leaf.row = row;
  if (sequence !== undefined) leaf.sequence = sequence;
  if (xy !== undefined) {
    leaf.xy = xy;
    leaf.fill = fill;
  }
  return [[leaf, 1]];
}

/**
 * A leaf's identity, for merging. **Canonical**, so two spellings of one reference merge: this is
 * the client's half of `LeafRef.cache_key` on the server, and the two exist for the same reason.
 */
export const leafKey = (leaf: LeafRef): string =>
  JSON.stringify([leaf.row ?? null, leaf.sequence ?? null, leaf.xy ?? null, leaf.fill ?? null]);

/**
 * The steps a run puts on the map: **interior, and the first bearer of their sequence** (`V145`).
 *
 * `endpoint` steps are dropped because those points are already on the map — they are what the
 * reader clicked. Duplicates are dropped because that is the feature as asked. What is *not*
 * dropped here is a step whose sequence merely reproduces an endpoint's own reconstruction:
 * `includeEndpointMatches` decides that, because it is a different question and the measurement
 * says it matters — over five random row pairs the first interior step matched `t = 0` in five
 * cases out of five.
 */
export function placeable(
  steps: readonly InterpolationStep[],
  includeEndpointMatches = false,
): InterpolationStep[] {
  return steps.filter(
    (step) =>
      step.endpoint === null &&
      step.duplicate_of === null &&
      (includeEndpointMatches || step.matches_endpoint === null),
  );
}

/** The `t` span one surviving step stands for, given the whole walk it came from. */
export function spanOf(
  steps: readonly InterpolationStep[],
  step: InterpolationStep,
): [number, number] {
  const covered = steps.filter((s) => s.index === step.index || s.duplicate_of === step.index);
  const ts = covered.map((s) => s.t);
  return [Math.min(...ts), Math.max(...ts)];
}

/** What a run would do to the decoded store for one view, so the panel can say so first (§5.1). */
export interface EvictionForecast {
  /** How many decoded points this view already holds. */
  readonly held: number;
  /** How many points the run would place. */
  readonly placing: number;
  /** How many existing marks in this view would be evicted to make room. `0` is the ordinary case. */
  readonly evicting: number;
  readonly cap: number;
}

/**
 * How many existing decoded points a run of `placing` would evict from a view already holding
 * `held` (§5.1).
 *
 * **This exists because eviction stops being invisible at this size.** `decoded.ts` evicts
 * oldest-first and silently, and `V123` accepted that for a point placed by one click. A 50-step
 * run can evict 50 in one gesture, which that reasoning does not cover — so the panel asks first.
 * It does not *refuse*: refusing would be `encoded.ts`'s policy applied to a store whose whole
 * point is that a click always works.
 */
export const evictionForecast = (held: number, placing: number): EvictionForecast => ({
  held,
  placing,
  evicting: Math.max(0, held + placing - CAP_PER_VIEW),
  cap: CAP_PER_VIEW,
});

/**
 * Where one point sits in its run, **computed from the run's current membership**.
 *
 * Not stored on the record, for the reason `trajectory.ts` gives about step numbers: a stored
 * ordinal is a second source of truth for the set's own order, and this set really does change —
 * the endpoint-match switch places and unplaces steps of a run that is already on the map.
 */
export interface RunPosition {
  readonly index: number;
  readonly total: number;
}

/**
 * `step i of n` for one record, from every record of its run.
 *
 * Ordered by `t`, which is the run's own ordering and the only one that means anything: the store
 * keeps records in write order, and a re-place writes the newly included steps last.
 */
export function runPosition(mark: DecodedMark, siblings: readonly DecodedMark[]): RunPosition | undefined {
  if (mark.run === undefined) return undefined;
  const ordered = siblings
    .filter((m) => m.run !== undefined)
    .sort((a, b) => (a.run as { t: number }).t - (b.run as { t: number }).t);
  const at = ordered.findIndex((m) => m.id === mark.id);
  return at < 0 ? undefined : { index: at + 1, total: ordered.length };
}

/** A run's id: what groups its steps in the store and in the panel. */
let runCounter = 0;
export function newRunId(): string {
  runCounter += 1;
  return `run-${Date.now().toString(36)}-${runCounter.toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}
